/*
 * 018 — rörelse, varukorg och öppettider.
 * Rörelse: Lenis (mjuk scroll) + GSAP/ScrollTrigger/SplitText. Allt rörligt hoppas över med "minska rörelse".
 * Varje sektion initieras via init(root), så att de också fungerar när de läggs till i temaeditorn.
 */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  const motion = hasGsap && !reduce;
  const kiosk = document.documentElement.classList.contains('is-kiosk');
  const root = document.documentElement;
  if (!motion) root.classList.add('no-motion');

  /* ---------- Lättläge för datorer som inte hinner med ----------
   * Hackar scrollningen märkbart (två perioder i rad med under ~35 bilder/s) slår sidan om till html.perf-lite
   * för resten av besöket: CSS tar bort suddiga glasytor och tunga effekter avslutas (se 'perf-lite'-händelsen). */
  const LITE_KEY = '018-lattlage';
  const isLite = () => root.classList.contains('perf-lite');
  try { if (sessionStorage.getItem(LITE_KEY)) root.classList.add('perf-lite'); } catch { /* privat läge */ }
  if (motion && !kiosk) {
    let lastScroll = 0, prev = 0, n = 0, slow = 0, bad = 0;
    addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
    (function watch(t) {
      if (isLite()) return;
      if (prev && t - lastScroll < 150 && t - prev < 500) {
        n++; if (t - prev > 28) slow++;
        if (n >= 60) {
          bad = slow > 30 ? bad + 1 : 0; n = slow = 0;
          if (bad >= 2) {
            root.classList.add('perf-lite');
            try { sessionStorage.setItem(LITE_KEY, '1'); } catch { /* privat läge */ }
            dispatchEvent(new Event('perf-lite'));
            return;
          }
        }
      }
      prev = t;
      requestAnimationFrame(watch);
    })(0);
  }
  if (hasGsap) gsap.registerPlugin(ScrollTrigger, ...(typeof SplitText !== 'undefined' ? [SplitText] : []));

  /* ---------- Mjuk scroll ---------- */
  let lenis = null;
  if (motion && typeof Lenis !== 'undefined') {
    lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 0.9 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }
  const lockScroll = on => {
    if (lenis) on ? lenis.stop() : lenis.start();
    root.style.overflow = on ? 'hidden' : '';
  };

  /* ---------- Pengar ---------- */
  const money = cents => {
    const fmt = (window.theme && theme.moneyFormat) || '{{amount_no_decimals}} kr';
    const n = cents / 100;
    const sv = (d) => n.toLocaleString('sv-SE', { minimumFractionDigits: d, maximumFractionDigits: d });
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => k.includes('no_decimals') ? sv(0) : sv(2)).replace(/<[^>]*>/g, '');
  };

  /* ---------- Sidhuvud ---------- */
  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const transparent = header.hasAttribute('data-transparent');
    let last = scrollY;
    const update = () => {
      const y = scrollY;
      header.classList.toggle('is-solid', !transparent || y > 40);
      header.classList.toggle('is-hidden', y > 400 && y > last && !root.classList.contains('menu-open'));
      last = y;
    };
    update();
    let queued = false;
    addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; update(); }); } }, { passive: true });

    const btn = header.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (btn && menu) {
      const set = open => {
        menu.classList.toggle('is-open', open);
        menu.inert = !open;
        btn.setAttribute('aria-expanded', open);
        btn.querySelector('[data-icon-open]').hidden = open;
        btn.querySelector('[data-icon-close]').hidden = !open;
        root.classList.toggle('menu-open', open);
        lockScroll(open);
      };
      menu.inert = true;
      btn.addEventListener('click', () => set(!menu.classList.contains('is-open')));
      addEventListener('keydown', e => { if (e.key === 'Escape' && menu.classList.contains('is-open')) set(false); });
    }
  }

  /* ---------- Rörelse per sektion ---------- */
  function initReveals(scope) {
    scope.querySelectorAll('[data-reveal]').forEach(el => {
      if (!motion) return void (el.style.opacity = 1);
      gsap.fromTo(el, { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 1.2, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
    });
    scope.querySelectorAll('[data-reveal-children]').forEach(el => {
      if (!motion) return void [...el.children].forEach(c => (c.style.opacity = 1));
      gsap.fromTo(el.children, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.2, stagger: 0.12, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
    });
    if (!motion || typeof SplitText === 'undefined') return;
    // Rubriker som byggs upp bokstav för bokstav (som React Bits SplitText)
    scope.querySelectorAll('[data-split]').forEach(el => {
      document.fonts.ready.then(() => {
        SplitText.create(el, {
          type: 'lines,words,chars', linesClass: 'split-line', mask: 'lines', autoSplit: true,
          // aria-label är inte tillåtet på <span>: rubrikrader döljs i stället (rubriken bär hela texten)
          aria: el.tagName === 'SPAN' ? 'hidden' : 'auto',
          onSplit: self => gsap.from(self.chars, {
            ...(innerWidth < 900 && !el.closest('.hero')
              ? { rotateX: -100, yPercent: 40, transformPerspective: 400, transformOrigin: '50% 100%', duration: 1.2, stagger: 0.03 }
              : { yPercent: 110, duration: 1.4, stagger: 0.035 }),
            ease: 'expo.out',
            scrollTrigger: el.closest('.hero') ? undefined : { trigger: el, start: 'top 85%', once: true },
          }),
        });
      });
    });
  }

  function initHero(scope) {
    const hero = scope.querySelector('.hero');
    if (!hero || !motion) return;
    const q = gsap.utils.selector(hero);
    const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
    tl.from(q('.hero__arch'), { clipPath: 'inset(100% 0% 0% 0% round 999px 999px 0 0)', duration: 1.8 })
      .from(q('.hero__arch img'), { scale: 1.25, duration: 2.2 }, 0)
      .from(q('.hero__inset'), { opacity: 0, y: 60, duration: 1.4 }, 0.6)
      .from(q('[data-hero-fade]'), { opacity: 0, y: 20, duration: 1.2, stagger: 0.1 }, 0.5);
    const scrub = { trigger: hero, start: 'top top', end: 'bottom top', scrub: true };
    gsap.to(q('.hero__arch img'), { yPercent: 12, ease: 'none', scrollTrigger: scrub });
    gsap.to(q('.hero__inset'), { yPercent: -35, ease: 'none', scrollTrigger: { ...scrub } });
    gsap.to(q('.hero__numerals'), { xPercent: -18, ease: 'none', scrollTrigger: { ...scrub } });
  }

  function initMarquee(scope) {
    scope.querySelectorAll('.marquee').forEach(el => {
      const track = el.querySelector('.marquee__track');
      if (!track || !motion) return;
      const alt = el.querySelector('.marquee__track--alt');
      const altLoop = alt ? gsap.fromTo(alt, { xPercent: -50 }, { xPercent: 0, duration: 70, ease: 'none', repeat: -1 }) : null;
      const loop = gsap.to(track, { xPercent: -50, duration: Number(el.dataset.speed) || 60, ease: 'none', repeat: -1 });
      // Scrollfart ger en knuff som klingar av; scrollriktningen bestämmer åt vilket håll bandet går
      let dir = 1, boost = 0;
      ScrollTrigger.create({ onUpdate(self) { dir = self.direction; boost = Math.max(boost, Math.min(Math.abs(self.getVelocity()) / 700, 2)); } });
      gsap.ticker.add(() => { boost *= 0.92; loop.timeScale(dir * (1 + boost)); altLoop?.timeScale(dir * (1 + boost)); });
      // Bandet rullar bara när det syns
      new IntersectionObserver(([e]) => { [loop, altLoop].forEach(t => t && (e.isIntersecting ? t.play() : t.pause())); }).observe(el);
    });
  }

  /* Dörren: ett valvformat fönster som växer tills det fyller skärmen och visar butiken */
  function initDoorway(scope) {
    scope.querySelectorAll('.doorway').forEach(el => {
      if (!motion) return;
      const q = gsap.utils.selector(el);
      const arch = { t: 18, x: 34, r: 999 };
      const frame = q('.doorway__frame')[0];
      const draw = () => { frame.style.clipPath = `inset(${arch.t}% ${arch.x}% 0% ${arch.x}% round ${arch.r}px ${arch.r}px 0 0)`; };
      const mobile = innerWidth < 768;
      if (mobile) arch.x = 18;
      draw();
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: 'bottom bottom', scrub: 1 } });
      tl.to(q('.doorway__intro'), { autoAlpha: 0, y: -40, duration: 0.25, ease: 'power1.in' }, 0)
        .to(arch, { t: 0, x: 0, r: 0, duration: 1, ease: 'power2.inOut', onUpdate: draw }, 0)
        .to(q('.doorway__frame img'), { scale: 1, duration: 1, ease: 'power2.inOut' }, 0)
        .to(q('.doorway__shade'), { opacity: 1, duration: 0.4 }, 0.6)
        .fromTo(q('.doorway__content'), { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.4 }, 0.75)
        .to({}, { duration: 0.3 });
    });
  }

  /* Horisontellt galleri som scrollas i sidled medan sektionen står still (bara på större skärmar) */
  function initHScroll(scope) {
    scope.querySelectorAll('.hscroll').forEach(el => {
      if (!motion) return;
      const track = el.querySelector('.hscroll__track');
      gsap.matchMedia().add('(min-width: 768px)', () => {
        const distance = () => Math.max(0, track.scrollWidth - innerWidth);
        gsap.to(track, {
          x: () => -distance(), ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: () => '+=' + distance(), pin: true, scrub: 1, invalidateOnRefresh: true },
        });
        gsap.utils.toArray(el.querySelectorAll('.card__media img')).forEach(img => {
          gsap.fromTo(img, { scale: 1.15 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: () => '+=' + distance(), scrub: true } });
        });
      });
    });
  }

  function initParallax(scope) {
    if (!motion) return;
    scope.querySelectorAll('[data-parallax]').forEach(el => {
      gsap.fromTo(el, { yPercent: -8 }, { yPercent: 8, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
  }


  /* Lookbook: på dator står bilden still i ett valv medan stegen byts med scrollen */
  function initLookbook(scope) {
    scope.querySelectorAll('[data-lookbook]').forEach(el => {
      if (!motion) return;
      const steps = [...el.querySelectorAll('.lookbook__step')];
      if (steps.length < 2) return;
      gsap.matchMedia().add('(min-width: 900px)', () => {
        el.classList.add('is-pinned');
        el.style.height = `${steps.length * 100 + 40}vh`;
        const closed = 'inset(100% 0% 0% 0% round 999px 999px 0 0)', open = 'inset(0% 0% 0% 0% round 999px 999px 0 0)';
        steps.forEach((s, i) => {
          if (!i) return;
          gsap.set(s.querySelector('.lookbook__media'), { clipPath: closed });
          gsap.set(s.querySelector('.lookbook__text'), { autoAlpha: 0, y: 40 });
        });
        const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: 'bottom bottom', scrub: 1 } });
        steps.forEach((s, i) => {
          if (!i) return;
          const at = i - 1 + 0.35;
          tl.to(s.querySelector('.lookbook__media'), { clipPath: open, duration: 0.65, ease: 'power2.inOut' }, at)
            .fromTo(s.querySelectorAll('.lookbook__media img'), { scale: 1.25 }, { scale: 1, duration: 0.65, ease: 'power2.out' }, at)
            .to(steps[i - 1].querySelector('.lookbook__text'), { autoAlpha: 0, y: -40, duration: 0.3, ease: 'power1.in' }, at)
            .to(s.querySelector('.lookbook__text'), { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power2.out' }, at + 0.3);
        });
        tl.to({}, { duration: 0.35 });
        return () => { el.classList.remove('is-pinned'); el.style.height = ''; gsap.set(el.querySelectorAll('.lookbook__media, .lookbook__text'), { clearProps: 'all' }); };
      });
    });
  }

  /* Produktkort och kategorivalv reser sig ur en valvform när de kommer in i bild. Pris och namn syns direkt. */
  function initCardReveal(scope) {
    if (!motion) return;
    const medias = [...scope.querySelectorAll('.product-grid .card__media, .arches .arch-card .media, .social__grid .media')].filter(m => !m.dataset.revealed);
    medias.forEach(m => (m.dataset.revealed = '1'));
    ScrollTrigger.batch(medias, {
      start: 'top 92%', once: true,
      onEnter: batch => gsap.fromTo(batch, { clipPath: 'inset(100% 0% 0% 0% round 999px 999px 0 0)' }, {
        clipPath: 'inset(0% 0% 0% 0% round 0px 0px 0 0)', duration: 1.3, stagger: 0.1, ease: 'expo.out',
        clearProps: 'clipPath',
      }),
    });
    // Kategorivalven i jämna kolumner glider lite långsammare, som en mjuk förskjutning
    scope.querySelectorAll('.arches').forEach(grid => {
      gsap.matchMedia().add('(min-width: 768px)', () => {
        gsap.to(grid.querySelectorAll(':scope > :nth-child(even)'), { y: -60, ease: 'none', scrollTrigger: { trigger: grid, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
    });
  }

  /* Bilder som går från svartvitt till färg medan man scrollar förbi */
  function initColorize(scope) {
    if (!motion) return;
    // Filter målar om hela bilden i varje bildruta – i lättläget visas bilderna direkt i färg
    if (isLite()) return;
    scope.querySelectorAll('[data-colorize]').forEach(img => {
      const tw = gsap.fromTo(img, { filter: 'grayscale(1)' }, { filter: 'grayscale(0)', ease: 'none', scrollTrigger: { trigger: img.closest('section') || img, start: 'top 75%', end: 'center 40%', scrub: true } });
      addEventListener('perf-lite', () => { tw.scrollTrigger?.kill(); tw.kill(); gsap.set(img, { clearProps: 'filter' }); }, { once: true });
    });
  }


  /* ---------- Mobil: egna scrolleffekter där desktop-versionerna inte passar ---------- */
  function initMobileMotion(scope) {
    if (!motion) return;
    gsap.matchMedia().add('(max-width: 899px)', () => {
      // Lookbook: korten fastnar överst och nästa kort glider upp över det förra, som krymper och mörknar
      scope.querySelectorAll('[data-lookbook]').forEach(lb => {
        const steps = [...lb.querySelectorAll('.lookbook__step')];
        steps.forEach((step, i) => {
          const img = step.querySelector('.lookbook__media img');
          if (img) gsap.fromTo(img, { scale: 1.25 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: step, start: 'top bottom', end: 'top top', scrub: true } });
          gsap.from(step.querySelector('.lookbook__text').children, { opacity: 0, y: 30, duration: 1, stagger: 0.07, ease: 'expo.out', scrollTrigger: { trigger: step, start: 'top 55%', once: true } });
          const next = steps[i + 1];
          if (!next) return;
          const st = { trigger: next, start: 'top bottom', end: 'top 10%', scrub: true };
          gsap.to(step, { scale: 0.88, ease: 'none', scrollTrigger: st });
          gsap.to(step.querySelector('.lookbook__shade'), { opacity: 0.55, ease: 'none', scrollTrigger: { ...st } });
        });
      });

      // Inredningsraden: korten glider in från höger
      scope.querySelectorAll('.hscroll').forEach(el => {
        gsap.from(el.querySelectorAll('.hscroll__track > *'), { x: 80, opacity: 0, duration: 1.2, stagger: 0.08, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 75%', once: true } });
      });

      ScrollTrigger.batch(scope.querySelectorAll('.arches .arch-card'), {
        start: 'top 92%', once: true,
        onEnter: batch => gsap.from(batch, { rotateX: 55, scale: 0.9, opacity: 0, transformPerspective: 700, transformOrigin: '50% 100%', duration: 1.4, stagger: 0.12, ease: 'expo.out', clearProps: 'transform,opacity,visibility' }),
      });
      ScrollTrigger.batch(scope.querySelectorAll('.product-grid .card'), {
        start: 'top 94%', once: true,
        onEnter: batch => batch.forEach((card, i) => gsap.from(card, { rotate: card.matches(':nth-child(even)') ? 4 : -4, scale: 0.92, duration: 1.3, delay: i * 0.08, ease: 'expo.out', clearProps: 'rotate,scale' })),
      });
      scope.querySelectorAll('.story .story__media .media').forEach(media => {
        gsap.fromTo(media, { clipPath: 'inset(6% 14% 0% 14% round 999px 999px 0 0)' }, { clipPath: 'inset(0% 0% 0% 0% round 999px 999px 0 0)', ease: 'none', scrollTrigger: { trigger: media, start: 'top 90%', end: 'top 25%', scrub: true } });
      });

      // Hero: valvbilden krymper mjukt och texten lyfter när man scrollar vidare
      scope.querySelectorAll('.hero').forEach(hero => {
        const st = { trigger: hero.querySelector('.hero__visual'), start: 'top 60%', end: 'bottom top', scrub: true };
        gsap.to(hero.querySelector('.hero__arch'), { scale: 0.9, yPercent: 6, ease: 'none', scrollTrigger: st });
        gsap.to(hero.querySelector('.hero__text'), { y: -50, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      });

      // Produktgrid: högerkolumnen glider lite långsammare
      scope.querySelectorAll('.product-grid').forEach(grid => {
        gsap.to(grid.querySelectorAll(':scope > :nth-child(even)'), { y: -28, ease: 'none', scrollTrigger: { trigger: grid, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
      if (innerWidth < 768) scope.querySelectorAll('.arches').forEach(grid => {
        gsap.to(grid.querySelectorAll(':scope > :nth-child(even)'), { y: -40, ease: 'none', scrollTrigger: { trigger: grid, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
    });
  }

  function initPageMotion() {
    if (!motion) return;
    const bar = document.querySelector('[data-scroll-progress]');
    if (bar) gsap.to(bar, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
    const giant = document.querySelector('.site-footer__giant');
    if (giant) gsap.from(giant, { yPercent: 70, ease: 'none', scrollTrigger: { trigger: '.site-footer', start: 'top bottom', end: 'bottom bottom', scrub: true } });
  }

  /* Progressband under inredningsraden på mobil, så att det syns att man kan svepa */
  function initSwipeProgress(scope) {
    scope.querySelectorAll('.hscroll').forEach(el => {
      const track = el.querySelector('.hscroll__track'), bar = el.querySelector('.hscroll__progress span');
      if (!bar) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const visible = track.clientWidth / track.scrollWidth;
        bar.style.transform = `scaleX(${Math.min(1, visible + (max > 0 ? (track.scrollLeft / max) * (1 - visible) : 1))})`;
      };
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  /* ---------- Öppettider (svensk tid) ---------- */
  const stores = (() => { try { return JSON.parse(document.getElementById('store-data').textContent); } catch { return []; } })();
  const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short' });
  const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DAY = ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'];
  const local = at => { const p = Object.fromEntries(fmt.formatToParts(at).map(x => [x.type, x.value])); return { date: `${p.year}-${p.month}-${p.day}`, wd: WD.indexOf(p.weekday), min: +p.hour * 60 + +p.minute }; };
  const RANGE = /(\d{1,2})(?:[:.](\d{2}))?\s*[–—-]\s*(\d{1,2})(?:[:.](\d{2}))?/;
  const parseRange = s => { const m = RANGE.exec(s || ''); return m ? [+m[1] * 60 + +(m[2] || 0), +m[3] * 60 + +(m[4] || 0)] : null; };
  const clock = min => `${Math.floor(min / 60)}${min % 60 ? ':' + String(min % 60).padStart(2, '0') : ''}`;
  const parseExtra = text => (text || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const m = /^(\d{4}-\d{2}-\d{2})\s+(.*)$/.exec(l);
    if (!m) return null;
    const closed = /^st[äa]ng/i.test(m[2]);
    const span = closed ? null : parseRange(m[2]);
    const note = m[2].replace(closed ? /^st[äa]ng\w*/i : RANGE, '').trim();
    return { date: m[1], span, note };
  }).filter(Boolean);
  stores.forEach(s => { s.spans = s.week.map(parseRange); s.extras = parseExtra(s.extra); });
  // Öregrund: dagar som butiken slår på från mobilen (går före temainställningarnas avvikande dagar samma datum)
  stores.filter(s => s.live).forEach(s => {
    fetch(s.live).then(r => r.json()).then(data => {
      const live = (data[s.slug] || []).map(d => ({ date: d.date, span: parseRange(`${d.from}–${d.to}`), note: '' }));
      if (!live.length) return;
      s.extras = [...s.extras.filter(e => !live.some(l => l.date === e.date)), ...live].sort((a, b) => a.date.localeCompare(b.date));
      paintStatus(); paintExtraDays();
    }).catch(() => {});
  });

  const spanOn = (s, date, wd) => { const ex = s.extras.find(e => e.date === date); return ex ? ex.span : s.spans[wd]; };
  function status(s, at) {
    const now = local(at), today = spanOn(s, now.date, now.wd);
    if (today && now.min >= today[0] && now.min < today[1]) return { open: true, today: true, label: 'Öppet nu', detail: `stänger ${clock(today[1])}` };
    if (today && now.min < today[0]) return { open: false, today: true, label: 'Stängt', detail: `öppnar i dag ${clock(today[0])}` };
    for (let d = 1; d <= 21; d++) {
      const n = local(new Date(at.getTime() + d * 864e5)), span = spanOn(s, n.date, n.wd);
      if (span) {
        const when = d === 1 ? 'i morgon' : d < 7 ? `på ${DAY[n.wd]}` : `${+n.date.slice(8)}/${+n.date.slice(5, 7)}`;
        return { open: false, label: 'Stängt', detail: `öppnar ${when} ${clock(span[0])}` };
      }
    }
    return { open: false, label: s.notice ? 'Stängt för tillfället' : 'Stängt' };
  }
  function paintStatus() {
    const now = new Date();
    document.querySelectorAll('[data-store-status]').forEach(el => {
      const s = stores.find(x => x.slug === el.dataset.storeStatus);
      if (!s) return;
      const st = status(s, now);
      if (el.hasAttribute('data-only-today')) { el.hidden = !st.today; el.closest('.announcement__stores')?.classList.toggle('has-two', !el.hidden); }
      el.classList.toggle('is-open', st.open);
      const text = el.querySelector('.store-status__text');
      text.textContent = (el.hasAttribute('data-with-name') ? `${s.name} · ` : '') + st.label;
      if (st.detail) { const d = document.createElement('span'); d.className = 'store-status__detail'; d.textContent = ` · ${st.detail}`; text.append(d); }
    });
  }
  function paintExtraDays() {
    const today = local(new Date()).date;
    document.querySelectorAll('[data-extra-days]').forEach(list => {
      const s = stores.find(x => x.slug === list.dataset.extraDays);
      if (!s) return;
      const upcoming = s.extras.filter(e => e.date >= today && e.span);
      // Butiker utan fasta tider: "Stängt för tillfället" byts mot en rubrik när det finns inlagda öppetdagar
      document.querySelectorAll(`[data-hours-notice="${s.slug}"]`).forEach(n => {
        n.dataset.text ??= n.textContent;
        n.textContent = upcoming.length ? 'Kommande öppetdagar' : n.dataset.text;
      });
      list.replaceChildren(...s.extras.filter(e => e.date >= today).slice(0, 6).map(e => {
        const li = document.createElement('li'), t = document.createElement('time');
        t.dateTime = e.date;
        t.textContent = new Date(e.date + 'T12:00:00').toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
        li.append(t, `${e.span ? `${clock(e.span[0])}–${clock(e.span[1])}` : 'Stängt'}${e.note ? ' · ' + e.note : ''}`);
        return li;
      }));
    });
  }

  /* ---------- Varukorg ---------- */
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let lastFocus = null;
  function openCart() {
    const d = drawer();
    if (!d) return void (location.href = theme.routes.cart);
    lastFocus = document.activeElement;
    d.classList.add('is-open'); d.inert = false;
    lockScroll(true);
    d.querySelector('[data-cart-close]')?.focus();
  }
  function closeCart() {
    const d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open'); d.inert = true;
    lockScroll(false);
    lastFocus?.focus();
  }
  function renderSections(sections) {
    if (!sections) return;
    const html = sections['cart-drawer'];
    if (html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-inner]');
      const current = document.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
    }
  }
  function setCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => (el.textContent = count));
  }
  async function cartRequest(url, body) {
    const d = drawer();
    d?.classList.add('is-loading');
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Något gick fel.');
      return data;
    } finally { d?.classList.remove('is-loading'); }
  }
  async function refreshCount() {
    const cart = await (await fetch(theme.routes.cart + '.js')).json();
    setCount(cart.item_count);
  }

  document.addEventListener('click', async e => {
    const t = e.target.closest('[data-cart-open], [data-cart-close], [data-qty], [data-remove]');
    if (!t) return;
    if (t.matches('[data-cart-open]')) { e.preventDefault(); openCart(); return; }
    if (t.matches('[data-cart-close]')) { e.preventDefault(); closeCart(); return; }
    e.preventDefault();
    const line = t.closest('[data-line]');
    const qty = t.matches('[data-remove]') ? 0 : Math.max(0, +line.dataset.quantity + +t.dataset.qty);
    const data = await cartRequest(theme.routes.cartChange + '.js', { id: line.dataset.key, quantity: qty });
    renderSections(data.sections);
    setCount(data.item_count);
  });
  addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });

  /* Används av outfitbyggaren: lägg flera varianter i varukorgen och öppna lådan */
  window.themeCart = {
    async add(items) {
      const data = await cartRequest(theme.routes.cartAdd + '.js', { items });
      renderSections(data.sections);
      await refreshCount();
      openCart();
    },
    money,
    lenis: () => lenis,
  };

  /* Produktformulär: lägg i varukorgen utan sidladdning och öppna lådan */
  document.addEventListener('submit', async e => {
    const form = e.target.closest('form.product-form');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]'), msg = form.querySelector('[data-form-msg]');
    const id = form.querySelector('[name="id"]').value;
    if (!id) { form.querySelector('[data-option-error]')?.removeAttribute('hidden'); return; }
    btn.setAttribute('aria-disabled', 'true');
    if (msg) msg.textContent = '';
    try {
      const data = await cartRequest(theme.routes.cartAdd + '.js', { items: [{ id: +id, quantity: 1 }] });
      renderSections(data.sections);
      await refreshCount();
      const img = form.closest('[data-product]')?.querySelector('.product__gallery .media:not([hidden]) img');
      const wait = flyToCart(img);
      if (wait) await new Promise(r => setTimeout(r, wait));
      openCart();
    } catch (err) {
      if (msg) msg.textContent = err.message;
    } finally { btn.removeAttribute('aria-disabled'); }
  });

  /* ---------- Variantval på produktsidan ---------- */
  function initProduct(scope) {
    scope.querySelectorAll('[data-product]').forEach(el => {
      const variants = JSON.parse(el.querySelector('[data-variants]').textContent);
      const form = el.querySelector('form.product-form');
      const idInput = form.querySelector('[name="id"]');
      const btn = form.querySelector('[type="submit"]');
      const label = btn.querySelector('[data-btn-label]');
      const priceEl = el.querySelector('[data-price]');
      const fieldsets = [...el.querySelectorAll('[data-option-index]')];
      const buybar = el.querySelector('[data-buybar]');
      const barBtn = buybar?.querySelector('[data-buybar-btn]');
      const selected = () => fieldsets.map(fs => fs.querySelector('input:checked')?.value ?? null);
      const notify = el.querySelector('[data-notify]');
      const stock = el.querySelector('[data-stock]');
      const what = notify?.querySelector('[data-notify-what]');
      if (what) what.dataset.title = what.value;

      function update() {
        const sel = selected();
        // Markera val som inte går att köpa i kombination med det som redan är valt
        fieldsets.forEach((fs, i) => fs.querySelectorAll('input').forEach(input => {
          const ok = variants.some(v => v.available && v.options[i] === input.value && sel.every((s, j) => j === i || s === null || v.options[j] === s));
          input.classList.toggle('is-unavailable', !ok);
        }));
        // Produkter utan val (bara en variant, t.ex. inredning) har inga fält – då gäller den enda varianten
        const v = !fieldsets.length ? (variants.find(x => x.available) || variants[0])
          : sel.includes(null) ? null : variants.find(x => x.options.every((o, i) => o === sel[i]));
        idInput.value = v && v.available ? v.id : '';
        showColor(sel);
        if (v) {
          priceEl.innerHTML = (v.compare_at_price > v.price ? `<s>${money(v.compare_at_price)}</s>` : '') + money(v.price);
          history.replaceState(null, '', `${location.pathname}?variant=${v.id}`);
        }
        label.textContent = v && !v.available ? 'Slutsåld' : sel.includes(null) ? 'Välj storlek' : kiosk ? 'Lägg i provlistan' : 'Lägg i varukorgen';
        if (buybar) {
          buybar.querySelector('[data-buybar-price]').innerHTML = priceEl.innerHTML;
          barBtn.textContent = v && !v.available ? 'Slutsåld' : sel.includes(null) ? 'Välj storlek' : kiosk ? 'Lägg i provlistan' : 'Lägg i varukorgen';
        }
        btn.toggleAttribute('aria-disabled', !!v && !v.available);
        if (kiosk) kioskStock(el, v);
        // Få kvar: "Sista i M" / "Bara 2 kvar i M" utifrån det riktiga lagret
        if (stock) {
          const max = +stock.dataset.max || 2;
          const one = !fieldsets.length || variants.length === 1;
          const sizeIdx = fieldsets.findIndex(fs => /storlek/i.test(fs.querySelector('legend')?.textContent || ''));
          const size = v && sizeIdx >= 0 ? v.options[sizeIdx] : '';
          const q = v && v.available ? v.qty : null;
          stock.hidden = !(q > 0 && q <= max);
          if (!stock.hidden) stock.textContent = q === 1 ? (size ? `Sista i ${size}` : one ? 'Sista exemplaret' : 'Sista kvar') : (size ? `Bara ${q} kvar i ${size}` : `Bara ${q} kvar`);
        }
        // Bevaka: visa formuläret när vald variant (eller hela produkten) är slutsåld
        if (notify && !notify.querySelector('[aria-invalid]')) {
          const soldOut = v ? !v.available : !variants.some(x => x.available);
          notify.hidden = !soldOut;
          if (what) what.value = what.dataset.title + (v && v.options.length && v.options[0] !== 'Default Title' ? ` – ${v.options.join(' / ')}` : '');
        }
        el.querySelector('[data-option-error]')?.setAttribute('hidden', '');
      }
      // Färgval: visa bara den valda färgens bilder (och bilder utan färg). Har färgen inga egna bilder visas alla.
      const gallery = el.querySelector('.product__gallery');
      const colorIndex = Number(gallery?.dataset.colorIndex ?? -1);
      const items = gallery ? [...gallery.querySelectorAll('[data-media-id]')] : [];
      let shownColor = gallery?.dataset.colorShown || null;
      function showColor(sel) {
        if (colorIndex < 0 || !items.length) return;
        const color = sel[colorIndex];
        if (!color || color === shownColor) return;
        shownColor = color;
        el.querySelector('[data-color-label]')?.replaceChildren(color);
        const own = items.filter(it => it.dataset.mediaColor === color);
        const show = own.length ? items.filter(it => !it.dataset.mediaColor || it.dataset.mediaColor === color) : items;
        const apply = () => {
          [...items].sort((a, b) => show.includes(b) - show.includes(a) || a.dataset.order - b.dataset.order).forEach(it => {
            it.hidden = !show.includes(it);
            gallery.append(it);
          });
          show.forEach(it => {
            it.querySelector('img')?.setAttribute('loading', 'eager');
            if (motion) { ScrollTrigger.getAll().filter(st => st.trigger === it).forEach(st => st.kill()); gsap.set(it, { opacity: 1, y: 0 }); }
            else it.style.opacity = 1;
          });
          if (innerWidth < 640) gallery.scrollTo({ left: 0 });
          if (motion) ScrollTrigger.refresh();
        };
        if (motion) gsap.to(gallery, { autoAlpha: 0, duration: 0.2, onComplete: () => { apply(); gsap.to(gallery, { autoAlpha: 1, duration: 0.5, ease: 'power2.out' }); } });
        else apply();
      }
      fieldsets.forEach(fs => fs.addEventListener('change', update));
      update();

      // Visa köpknappen längst ner när den vanliga knappen har scrollats förbi
      if (buybar) {
        new IntersectionObserver(([en]) => {
          const show = !en.isIntersecting && en.boundingClientRect.top < 0;
          buybar.classList.toggle('is-visible', show);
          buybar.setAttribute('aria-hidden', !show);
          barBtn.tabIndex = show ? 0 : -1;
        }).observe(btn);
        barBtn.addEventListener('click', () => {
          if (idInput.value) return form.requestSubmit();
          const missing = fieldsets.find(fs => !fs.querySelector('input:checked'));
          const target = missing || form;
          if (lenis) lenis.scrollTo(target, { offset: -120 }); else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (missing) { missing.classList.add('is-flash'); setTimeout(() => missing.classList.remove('is-flash'), 1600); }
        });
      }
    });
  }

  /* Liknande produkter laddas efteråt från Shopifys rekommendationer */
  function initRecommendations(scope) {
    scope.querySelectorAll('[data-recommendations]').forEach(async el => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      const html = await (await fetch(el.dataset.url)).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initReveals(el); initCardReveal(el); if (hasGsap) ScrollTrigger.refresh(); }
    });
  }

  /* Sortering och filter på kollektionssidan */
  document.addEventListener('change', e => {
    const s = e.target.closest('[data-sort]');
    if (!s) return;
    const url = new URL(location.href);
    url.searchParams.set('sort_by', s.value);
    url.searchParams.delete('page');
    location.href = url.toString();
  });

  /* ---------- Säsong just nu: vädret (Open-Meteo, sparas 30 min) och månaden ----------
     Under 10° = kallt, över 18° = varmt (strikt: plagg för fel säsong visas inte).
     Däremellan, eller utan väder, avgör månaden – strikt bara mitt i vintern (nov–mar) och sommaren (jun–aug). */
  let seasonPromise = null;
  function season() {
    if (seasonPromise) return seasonPromise;
    const month = new Date().getMonth() + 1;
    const byMonth = { now: month >= 4 && month <= 9 ? 'varmt' : 'kallt', strict: month >= 11 || month <= 3 || (month >= 6 && month <= 8), temp: null };
    const cfg = window.theme?.season;
    if (!cfg?.lat) return (seasonPromise = Promise.resolve(byMonth));
    const key = `018-vader-${cfg.lat},${cfg.lon}`;
    const fromTemp = t => (t <= 10 ? { now: 'kallt', strict: true, temp: t } : t >= 18 ? { now: 'varmt', strict: true, temp: t } : { ...byMonth, strict: false, temp: t });
    try { const c = JSON.parse(sessionStorage.getItem(key)); if (c && Date.now() - c.at < 30 * 60 * 1000) return (seasonPromise = Promise.resolve(fromTemp(c.t))); } catch { /* ingen lagring */ }
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), 3000);
    seasonPromise = fetch(`https://api.open-meteo.com/v1/forecast?latitude=${cfg.lat}&longitude=${cfg.lon}&current=temperature_2m&timezone=Europe%2FStockholm`, { signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => {
        const t = d.current.temperature_2m;
        try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), t })); } catch { /* ingen lagring */ }
        return fromTemp(t);
      })
      .catch(() => byMonth);
    return seasonPromise;
  }
  // Passar ett plagg/en bild med säsongen s ('kallt', 'varmt' eller '' = året runt)?
  const fitsSeason = (tag, s) => !tag || tag === s.now || !s.strict;

  /* Lookbook: varje steg har en bild för kallt och en för varmt väder, och plagglistan filtreras */
  function initSeasonal(scope) {
    const pickers = [...scope.querySelectorAll('[data-season-pick]')];
    const lists = [...scope.querySelectorAll('[data-season-list]')];
    if (!pickers.length && !lists.length) return;
    season().then(s => {
      pickers.forEach(p => {
        const alts = [...p.querySelectorAll('[data-season]')];
        const pick = alts.find(a => a.dataset.season === s.now) || alts.find(a => !a.dataset.season) || alts[0];
        alts.forEach(a => { a.hidden = a !== pick; });
      });
      lists.forEach(list => {
        const max = +list.dataset.max || 3;
        const items = [...list.children];
        const ok = items.filter(li => fitsSeason(li.dataset.season, s));
        // Matchande säsong först, sedan året runt
        ok.sort((a, b) => (b.dataset.season === s.now) - (a.dataset.season === s.now));
        items.forEach(li => { li.hidden = !ok.slice(0, max).includes(li); });
        ok.slice(0, max).forEach(li => list.append(li));
      });
      if (hasGsap) ScrollTrigger.refresh();
    });
  }

  /* ---------- Butiksläge (iPad i butiken) ----------
     Varukorgen blir en provlista som visas för personalen (med QR-kod så att kunden kan ta med den i mobilen),
     länkar bort från sidan stängs av och allt nollställs när ingen har rört skärmen på en stund. */
  const K = window.theme?.kiosk || {};
  const esc = t => String(t ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  // Finns varianten här i butiken? (Shopifys hämta-i-butik-lager för butiker med upphämtning, dvs. Uppsala)
  const hereCache = new Map();
  function hereStatus(variantId) {
    if (!hereCache.has(variantId)) {
      hereCache.set(variantId, fetch(`${K.stock}${variantId}/?section_id=kiosk-lager`).then(r => (r.ok ? r.text() : ''))
        .then(html => { const m = html.match(/\{[^<]*"butik"[^<]*\}/); return m ? JSON.parse(m[0]) : null; }).catch(() => null));
    }
    return hereCache.get(variantId);
  }
  function kioskStock(el, v) {
    const out = el.querySelector('[data-kiosk-stock]');
    if (!out) return;
    if (!v) { out.textContent = ''; return; }
    if (!v.available) { out.textContent = ''; return; }
    const id = v.id;
    out.dataset.for = id;
    hereStatus(id).then(r => {
      if (out.dataset.for !== String(id)) return;
      out.className = 'product__here kiosk-only' + (r?.butik ? ' is-here' : r?.butik === false ? ' is-elsewhere' : '');
      out.textContent = r?.butik ? 'Finns här i butiken – fråga oss så hämtar vi den' : r?.butik === false ? 'Finns inte här just nu – vi kan ta hit den till butiken' : '';
      if (r?.butik === false && K.order) {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'btn btn--ink product__order'; b.textContent = 'Beställ till butiken – betala när du hämtar';
        b.addEventListener('click', () => K.order(id));
        out.append(document.createElement('br'), b);
      }
    });
  }

  function initKiosk() {
    if (!kiosk) return;
    const onStart = K.start && location.pathname === new URL(K.start, location.origin).pathname;
    // Dockan: Start och Provlista – stora knappar för fingrar
    const dock = document.createElement('nav');
    dock.className = 'kiosk-dock';
    dock.setAttribute('aria-label', 'Butiksläge');
    const home = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10v10h13V10"/></svg>';
    dock.innerHTML = `${onStart ? '' : `<a href="${esc(K.start)}" class="kiosk-dock__btn kiosk-dock__btn--home">${home}<span>Till start</span></a>`}<button type="button" class="kiosk-dock__btn kiosk-dock__btn--ink" data-cart-open><span>Provlista (<span data-cart-count>0</span>)</span></button>`;
    document.body.append(dock);
    refreshCount();

    // Länkar bort från sidan (Instagram, mejl …) öppnas inte på butikens iPad
    document.addEventListener('click', e => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || a.target === '_blank' || /^(mailto|tel):/.test(a.getAttribute('href'))) {
        e.preventDefault(); e.stopPropagation();
        toast('Den länken öppnas inte här i butiken.');
      }
    }, true);

    // Loggan leder till iPadens startskärm. Personalens utgång: håll fingret på loggan i tre sekunder.
    const logo = document.querySelector('.site-header__logo');
    if (logo && K.start) logo.href = K.start;
    let hold = 0, held = false;
    logo?.addEventListener('pointerdown', () => { held = false; hold = setTimeout(() => { held = true; if (confirm('Avsluta butiksläget på den här iPaden?')) { try { localStorage.removeItem('018-butik'); } catch { /* */ } location.href = '/'; } }, 3000); });
    ['pointerup', 'pointerleave', 'pointercancel'].forEach(t => logo?.addEventListener(t, () => clearTimeout(hold)));
    logo?.addEventListener('click', e => { if (held) e.preventDefault(); });
    logo?.addEventListener('contextmenu', e => e.preventDefault());

    // Nollställ när ingen har rört skärmen: fråga först i tio sekunder
    const IDLE = (K.idle || 90) * 1000;
    let idleTimer = 0, warnTimer = 0, warn = null;
    const reset = async () => {
      try { await fetch(`${theme.routes.root.replace(/\/$/, '')}/cart/clear.js`, { method: 'POST' }); } catch { /* */ }
      try { localStorage.removeItem('018-favoriter'); sessionStorage.clear(); } catch { /* */ }
      location.href = K.start || '/';
    };
    const arm = () => {
      clearTimeout(idleTimer); clearTimeout(warnTimer);
      warn?.remove(); warn = null;
      idleTimer = setTimeout(async () => {
        const count = +(document.querySelector('[data-cart-count]')?.textContent || 0);
        if (onStart && !count && !scrollY) return arm();  // redan nollställd
        warn = document.createElement('div');
        warn.className = 'kiosk-warn';
        warn.innerHTML = '<div><p class="display">Är du kvar?</p><p>Vi börjar om om <span data-left>10</span> sekunder.</p><button type="button" class="btn btn--ink">Jag är kvar</button></div>';
        document.body.append(warn);
        let left = 10;
        const tick = () => { left--; const s = warn?.querySelector('[data-left]'); if (s) s.textContent = left; if (left <= 0) reset(); else warnTimer = setTimeout(tick, 1000); };
        warnTimer = setTimeout(tick, 1000);
      }, IDLE);
    };
    ['pointerdown', 'keydown', 'wheel', 'touchmove'].forEach(t => addEventListener(t, arm, { passive: true }));
    arm();

    // Visa för personalen
    let dlg = null;
    async function showStaff() {
      const cart = await (await fetch(theme.routes.cart + '.js')).json();
      if (!dlg) {
        dlg = document.createElement('dialog');
        dlg.className = 'kiosk-staff';
        dlg.setAttribute('aria-label', 'Provlista för personalen');
        document.body.append(dlg);
        dlg.addEventListener('close', () => lenis?.start());
      }
      const items = cart.items;
      const statuses = await Promise.all(items.map(i => hereStatus(i.variant_id)));
      const url = `${location.origin}/cart/${items.map(i => `${i.variant_id}:${i.quantity}`).join(',')}`;
      dlg.innerHTML = `
        <div class="kiosk-staff__head">
          <p class="eyebrow">Visa för personalen</p>
          <h2 class="display">Det här vill jag <em>prova</em></h2>
        </div>
        <ul class="kiosk-staff__list">${items.map((i, k) => {
          const s = statuses[k];
          const opts = (i.options_with_values || []).filter(o => o.value !== 'Default Title').map(o => `<span><small>${esc(o.name)}</small>${esc(o.value)}</span>`).join('');
          return `<li>${i.image ? `<img src="${esc(i.image)}${i.image.includes('?') ? '&' : '?'}width=300" alt="" width="120" height="150">` : ''}
            <div><strong>${esc(i.product_title)}</strong><div class="kiosk-staff__opts">${opts}${i.quantity > 1 ? `<span><small>Antal</small>${i.quantity}</span>` : ''}</div>
            ${s?.butik ? '<p class="kiosk-staff__here is-here">Finns här i butiken</p>' : s?.butik === false ? '<p class="kiosk-staff__here is-elsewhere">Finns inte här – vi kan ta hit den</p>' : ''}</div></li>`;
        }).join('')}</ul>
        ${orderForm(items.filter((_, k) => statuses[k]?.butik === false))}
        <div class="kiosk-staff__foot">
          <div class="kiosk-staff__qr" data-qr aria-hidden="true"></div>
          <p><strong>Ta med listan i mobilen</strong><br>Skanna koden med kameran – då kan du handla hemifrån när du vill.</p>
          <div class="kiosk-staff__btns">
            <button type="button" class="btn btn--line" data-staff-close>Tillbaka</button>
            <button type="button" class="btn btn--ink" data-staff-done>Klar – börja om</button>
          </div>
        </div>`;
      dlg.querySelector('[data-staff-close]').addEventListener('click', () => dlg.close());
      dlg.querySelector('[data-staff-done]').addEventListener('click', reset);
      closeCart();
      dlg.showModal();
      lenis?.stop();
      // QR-koden: biblioteket laddas först när det behövs
      const draw = () => { const q = window.qrcode(0, 'M'); q.addData(url); q.make(); dlg.querySelector('[data-qr]').innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true }); };
      if (window.qrcode) draw();
      else { const sc = document.createElement('script'); sc.src = K.qr; sc.onload = draw; document.head.append(sc); }
    }
    document.addEventListener('click', e => { if (e.target.closest('[data-kiosk-show]')) { e.preventDefault(); showStaff(); } });

    /* Beställ till butiken: varor som inte finns här tas hit (t.ex. från Öregrund), kunden betalar vid hämtning.
       Skickas som ett kontaktformulär till butikens mejl – ingen betalning på iPaden. */
    function orderForm(away) {
      if (!away.length) return '';
      const lines = away.map(i => `${i.product_title}${(i.options_with_values || []).filter(o => o.value !== 'Default Title').map(o => ` – ${o.value}`).join('')}${i.quantity > 1 ? ` × ${i.quantity}` : ''}`).join('; ');
      return `<form class="kiosk-order" method="post" action="/contact#kiosk-order" accept-charset="UTF-8">
        <input type="hidden" name="form_type" value="contact"><input type="hidden" name="utf8" value="✓">
        <input type="hidden" name="contact[Butiksbeställning (iPad)]" value="${esc(lines)}">
        <input type="hidden" name="contact[body]" value="Beställning från butikens iPad: ta hit varorna till butiken och kontakta kunden. Kunden betalar vid hämtning.">
        <p class="kiosk-order__title">Finns inte här just nu? <em>Vi tar hit det.</em></p>
        <p class="kiosk-order__text">${away.length === 1 ? esc(away[0].product_title) : `${away.length} plagg`} – fyll i dina uppgifter så hör vi av oss när det har kommit till butiken. Du betalar först när du hämtar.</p>
        <div class="kiosk-order__fields">
          <input type="text" name="contact[Namn]" placeholder="Namn" required autocomplete="off">
          <input type="tel" name="contact[Mobil]" placeholder="Mobilnummer" required autocomplete="off">
          <input type="email" name="contact[email]" placeholder="E-post" required autocomplete="off">
        </div>
        <button type="submit" class="btn btn--ink">Beställ till butiken</button>
      </form>`;
    }
    // Lägg varianten i provlistan och öppna beställningen direkt (knappen på produktsidan)
    K.order = async id => {
      try { await cartRequest(theme.routes.cartAdd + '.js', { items: [{ id: +id, quantity: 1 }] }); await refreshCount(); } catch { /* finns redan */ }
      showStaff();
    };
    // Tillbaka efter en skickad beställning: tacka, töm provlistan
    if (new URLSearchParams(location.search).get('contact_posted') === 'true' && location.hash.includes('kiosk-order')) {
      history.replaceState(null, '', location.pathname);
      fetch(`${theme.routes.root.replace(/\/$/, '')}/cart/clear.js`, { method: 'POST' }).then(refreshCount).catch(() => {});
      const t = document.createElement('dialog');
      t.className = 'kiosk-staff kiosk-thanks';
      t.innerHTML = '<p class="eyebrow">Tack för din beställning</p><h2 class="display">Vi tar hit det <em>åt dig</em></h2><p>Vi hör av oss så fort det har kommit till butiken. Du betalar när du hämtar – och provar gärna först.</p><button type="button" class="btn btn--ink">Klar</button>';
      document.body.append(t); t.showModal();
      t.querySelector('button').addEventListener('click', () => { location.href = K.start || '/'; });
    }
  }

  function toast(text) {
    const t = document.createElement('p');
    t.className = 'kiosk-toast';
    t.setAttribute('role', 'status');
    t.textContent = text;
    document.body.append(t);
    setTimeout(() => t.remove(), 2600);
  }

  /* ---------- Bilden glider in i produktsidan (View Transitions mellan sidor) ---------- */
  // Kortets bild får samma namn som produktsidans första bild precis när man klickar – webbläsaren animerar mellan dem.
  if (window.theme?.effects?.viewTransitions && !reduce) {
    document.addEventListener('click', e => {
      const link = e.target.closest('.card__link');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      document.querySelectorAll('[style*="produktbild"]').forEach(el => { el.style.viewTransitionName = 'none'; });
      const media = link.querySelector('.card__media');
      if (media) media.style.viewTransitionName = 'produktbild';
    });
    // Tillbaka-knappen visar sidan från cachen – ta bort namnet så att nästa klick blir rätt
    addEventListener('pageshow', () => document.querySelectorAll('.card__media[style*="produktbild"]').forEach(el => { el.style.viewTransitionName = ''; }));
  }

  /* ---------- Plagget flyger till varukorgen ---------- */
  function flyToCart(img) {
    if (!window.theme?.effects?.cartFly || reduce || !hasGsap || !img) return;
    const target = [...document.querySelectorAll('[data-cart-open]')].find(a => a.offsetParent);
    if (!target) return;
    const from = img.getBoundingClientRect(), to = target.getBoundingClientRect();
    if (!from.width) return;
    const ghost = img.cloneNode();
    ghost.removeAttribute('srcset');
    ghost.src = img.currentSrc || img.src;
    Object.assign(ghost.style, { position: 'fixed', zIndex: 80, left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px`,
      objectFit: 'cover', borderRadius: '999px 999px 0 0', pointerEvents: 'none', margin: 0 });
    ghost.setAttribute('aria-hidden', 'true');
    document.body.append(ghost);
    const size = 44;
    gsap.timeline({ onComplete: () => ghost.remove() })
      .to(ghost, { duration: 0.8, ease: 'power3.inOut', left: to.left + to.width / 2 - size / 2, top: to.top + to.height / 2 - size / 2, width: size, height: size, borderRadius: '50%' })
      .to(ghost, { duration: 0.2, autoAlpha: 0, scale: 0.4 }, '-=0.1');
    gsap.fromTo(document.querySelectorAll('[data-cart-count]'), { scale: 1 }, { scale: 1.6, duration: 0.2, delay: 0.75, yoyo: true, repeat: 1, ease: 'power2.out', transformOrigin: '50% 60%' });
    return 900;
  }

  /* ---------- Handskrivna lappar: pilen ritas när lappen syns ---------- */
  function initNotes(scope) {
    const notes = [...scope.querySelectorAll('[data-note]:not(.is-drawn)')];
    if (!notes.length) return;
    if (reduce || !('IntersectionObserver' in window)) { notes.forEach(n => n.classList.add('is-drawn')); return; }
    const io = new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-drawn'); io.unobserve(en.target); }
    }), { threshold: 0.6 });
    notes.forEach(n => io.observe(n));
  }

  /* ---------- Färgprickar på produktkorten: hovra = visa färgen, klick = produkten i den färgen ---------- */
  const dotPreview = dot => {
    const card = dot.closest('.card');
    const img = card?.querySelector('.card__media img');
    if (!img || !dot.dataset.src) return;
    if (!img.dataset.origSrc) { img.dataset.origSrc = img.getAttribute('src'); img.dataset.origSrcset = img.srcset; }
    img.srcset = dot.dataset.srcset; img.src = dot.dataset.src;
    card.classList.add('is-previewing');
    card.querySelectorAll('.card__dot').forEach(d => d.classList.toggle('is-active', d === dot));
  };
  const dotRestore = list => {
    const card = list.closest('.card');
    const img = card?.querySelector('.card__media img');
    if (img?.dataset.origSrc) { img.srcset = img.dataset.origSrcset; img.src = img.dataset.origSrc; delete img.dataset.origSrc; }
    card?.classList.remove('is-previewing');
    card?.querySelectorAll('.card__dot.is-active').forEach(d => d.classList.remove('is-active'));
  };
  document.addEventListener('pointerover', e => { const d = e.target.closest?.('.card__dot'); if (d && e.pointerType !== 'touch') dotPreview(d); });
  document.addEventListener('focusin', e => { const d = e.target.closest?.('.card__dot'); if (d) dotPreview(d); });
  document.addEventListener('pointerout', e => {
    const l = e.target.closest?.('[data-card-colors]');
    if (l && !l.contains(e.relatedTarget)) dotRestore(l);
  });
  document.addEventListener('focusout', e => {
    const l = e.target.closest?.('[data-card-colors]');
    if (l && !l.contains(e.relatedTarget)) dotRestore(l);
  });

  /* ---------- Favoriter (sparas bara i besökarens webbläsare) ---------- */
  const FAV_KEY = '018-favoriter';
  const favs = {
    list() {
      try { const l = JSON.parse(localStorage.getItem(FAV_KEY)); return Array.isArray(l) ? l.filter(h => typeof h === 'string') : []; }
      catch { return []; }
    },
    save(l) {
      try { localStorage.setItem(FAV_KEY, JSON.stringify(l)); } catch { /* privat läge: favoriter gäller bara tills sidan laddas om */ }
      memory = l;
      paintFavs();
      document.dispatchEvent(new CustomEvent('favs:change'));
    },
    toggle(h) {
      const l = favs.current();
      const i = l.indexOf(h);
      if (i >= 0) l.splice(i, 1); else l.unshift(h);
      favs.save(l);
      return i < 0;
    },
    current() { return memory ? [...memory] : favs.list(); }
  };
  let memory = null;
  function paintFavs(scope = document) {
    const l = favs.current();
    scope.querySelectorAll('[data-fav]').forEach(b => {
      const on = l.includes(b.dataset.fav);
      b.setAttribute('aria-pressed', on);
      const lab = b.querySelector('[data-fav-label]');
      if (lab) lab.textContent = on ? 'Sparad som favorit' : 'Spara som favorit';
    });
    document.querySelectorAll('[data-fav-count]').forEach(c => { c.textContent = l.length; });
  }
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-fav]');
    if (!b) return;
    e.preventDefault();
    if (favs.toggle(b.dataset.fav) && !reduce) { b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop'); }
  });
  addEventListener('storage', e => { if (e.key === FAV_KEY) { memory = null; paintFavs(); document.dispatchEvent(new CustomEvent('favs:change')); } });

  /* Favoritsidan: korten hämtas med produktmallen product.card. ?lista=a,b visar en delad lista. */
  function initFavorites(scope) {
    scope.querySelectorAll('[data-favorites]').forEach(async root => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const grid = root.querySelector('[data-fav-grid]');
      const empty = root.querySelector('[data-fav-empty]');
      const bar = root.querySelector('[data-fav-bar]');
      const shared = root.querySelector('[data-fav-shared]');
      const msg = root.querySelector('[data-fav-msg]');
      const param = new URLSearchParams(location.search).get('lista');
      const sharedList = param ? [...new Set(param.split(',').map(h => h.trim()).filter(h => /^[a-z0-9-]+$/.test(h)))].slice(0, 40) : null;
      const say = t => { msg.textContent = t; clearTimeout(say.t); say.t = setTimeout(() => { msg.textContent = ''; }, 4000); };

      function state() {
        const n = grid.querySelectorAll('.card').length;
        empty.hidden = n > 0;
        bar.hidden = n === 0 || !!sharedList;
      }
      async function render(list) {
        grid.setAttribute('aria-busy', 'true');
        const parts = await Promise.all(list.map(h => fetch(`${root.dataset.productsUrl}${h}?view=card`)
          .then(async r => ({ h, html: r.ok ? await r.text() : '', gone: r.status === 404 }))
          .catch(() => ({ h, html: '', gone: false }))));
        grid.innerHTML = parts.map(p => p.html).join('');
        grid.removeAttribute('aria-busy');
        // Produkter som tagits bort ur butiken rensas ur den egna listan
        if (!sharedList && parts.some(p => p.gone)) favs.save(list.filter(h => !parts.find(p => p.h === h).gone));
        paintFavs(grid);
        state();
        initCardReveal(grid);
        if (hasGsap) ScrollTrigger.refresh();
      }

      if (sharedList) {
        shared.hidden = false;
        shared.querySelector('[data-fav-import]').addEventListener('click', () => {
          const mine = favs.current();
          favs.save([...sharedList.filter(h => !mine.includes(h)), ...mine]);
          shared.querySelector('[data-fav-import-msg]').textContent = 'Sparat i dina favoriter.';
        });
      } else {
        // Tar man bort ett hjärta på favoritsidan försvinner kortet
        document.addEventListener('favs:change', () => {
          const l = favs.current();
          grid.querySelectorAll('.card').forEach(c => { const b = c.querySelector('[data-fav]'); if (b && !l.includes(b.dataset.fav)) c.remove(); });
          state();
        });
      }
      root.querySelector('[data-fav-share]')?.addEventListener('click', async () => {
        const url = `${location.origin}${location.pathname}?lista=${favs.current().join(',')}`;
        if (navigator.share) {
          try { await navigator.share({ title: document.title, text: 'Mina favoriter från 018 Interiör & Design', url }); } catch { /* avbrutet */ }
          return;
        }
        try { await navigator.clipboard.writeText(url); say('Länken är kopierad – klistra in den i ett sms eller mejl.'); }
        catch { prompt('Kopiera länken:', url); }
      });
      await render(sharedList || favs.current());
    });
  }

  /* ---------- Bildspel som Instagram-stories ---------- */
  function initStories(scope) {
    scope.querySelectorAll('[data-stories]').forEach(root => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      let data;
      try { data = JSON.parse(root.querySelector('[data-stories-data]').textContent); } catch { return; }
      const dlg = root.querySelector('[data-stories-viewer]');
      const img = dlg.querySelector('[data-stories-img]');
      const bars = dlg.querySelector('[data-stories-bars]');
      const DURATION = 5000;
      let s = 0, i = 0, start = 0, elapsed = 0, paused = false, raf = 0;

      function paint() {
        const story = data[s], item = story.items[i];
        bars.innerHTML = story.items.map((_, k) => `<span><i style="width:${k < i ? 100 : 0}%"></i></span>`).join('');
        dlg.querySelector('[data-stories-title]').textContent = story.title;
        img.src = item.img;
        img.alt = item.title;
        dlg.querySelector('[data-stories-name]').textContent = item.title;
        dlg.querySelector('[data-stories-price]').textContent = item.price;
        dlg.querySelector('[data-stories-link]').href = item.url;
        const next = story.items[i + 1] || data[s + 1]?.items[0];
        if (next) new Image().src = next.img;
        elapsed = 0; start = performance.now();
      }
      function tick(now) {
        if (!dlg.open) return;
        if (!paused) {
          const p = Math.min(1, (elapsed + now - start) / DURATION);
          const bar = bars.children[i]?.firstElementChild;
          if (bar) bar.style.width = `${p * 100}%`;
          if (p >= 1) { go(1); }
        }
        raf = requestAnimationFrame(tick);
      }
      function go(dir) {
        const story = data[s];
        if (dir > 0 && i < story.items.length - 1) i++;
        else if (dir > 0) { if (s < data.length - 1) { s++; i = 0; } else return close(); }
        else if (i > 0) i--;
        else if (s > 0) { s--; i = data[s].items.length - 1; }
        paint();
      }
      function open(n) {
        if (!data[n]?.items.length) return;
        s = n; i = 0; paused = false;
        dlg.showModal();
        lenis?.stop();
        paint();
        if (reduce) return;  // ingen automatisk bläddring för den som valt minska rörelse
        cancelAnimationFrame(raf); raf = requestAnimationFrame(tick);
      }
      function close() { cancelAnimationFrame(raf); if (dlg.open) dlg.close(); }
      dlg.addEventListener('close', () => { cancelAnimationFrame(raf); lenis?.start(); });
      root.querySelectorAll('[data-story]').forEach(b => b.addEventListener('click', () => open(+b.dataset.story)));
      dlg.querySelector('[data-stories-close]').addEventListener('click', close);
      dlg.querySelector('[data-stories-next]').addEventListener('click', () => { if (!held) go(1); });
      dlg.querySelector('[data-stories-prev]').addEventListener('click', () => { if (!held) go(-1); });
      dlg.addEventListener('keydown', e => { if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); });
      // Håll inne för att pausa, svep nedåt för att stänga
      const stage = dlg.querySelector('[data-stories-stage]');
      let downAt = 0, downY = 0, held = false, holdTimer = 0;
      stage.addEventListener('pointerdown', e => {
        downAt = performance.now(); downY = e.clientY; held = false;
        holdTimer = setTimeout(() => { held = true; paused = true; elapsed += performance.now() - start; }, 220);
      });
      const release = e => {
        clearTimeout(holdTimer);
        if (paused && held) { paused = false; start = performance.now(); setTimeout(() => { held = false; }, 0); }
        if (e && e.clientY - downY > 90) close();
      };
      stage.addEventListener('pointerup', release);
      stage.addEventListener('pointercancel', () => release());
    });
  }

  /* ---------- Dagens outfit: en look per dag ur dagens-outfits.json, som passar säsongen ---------- */
  function initDailyOutfit(scope) {
    scope.querySelectorAll('[data-daily]').forEach(async root => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      let list;
      try { list = await (await fetch(root.dataset.list)).json(); } catch { return; }
      if (!Array.isArray(list) || !list.length) return;
      const s = await season();
      // Looker för rätt säsong först (strikt väder: bara de), därefter året runt
      const fitting = list.filter(l => fitsSeason(l.season, s));
      const pool = [...fitting.filter(l => l.season === s.now), ...fitting.filter(l => l.season !== s.now)];
      if (!pool.length) return;
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Stockholm' }));
      const day = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 864e5);
      const matching = pool.filter(l => l.season === s.now).length;
      const start = matching ? day % matching : day % pool.length;
      const esc = t => String(t).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      for (let i = 0; i < Math.min(pool.length, 12); i++) {
        const look = pool[(start + i) % pool.length];
        const items = await Promise.all(look.items.map(it => fetch(`${root.dataset.productsUrl}${it.handle}.js`).then(r => (r.ok ? r.json() : null)).catch(() => null)));
        // Hela looken måste gå att köpa i rätt färg
        const ok = items.every((p, k) => p && p.variants.some(v => v.available && (!look.items[k].color || v.options.includes(look.items[k].color))));
        if (!ok) continue;
        const img = root.querySelector('[data-daily-img]');
        img.src = `${root.dataset.server}/img/${look.key}`;
        img.alt = `Modell i ${look.items.map(x => x.title).join(', ')}`;
        root.querySelector('[data-daily-date]').textContent = now.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' }) + (s.temp != null ? ` · ${Math.round(s.temp)}°` : '');
        let total = 0;
        root.querySelector('[data-daily-items]').innerHTML = items.map((p, k) => {
          const c = look.items[k].color;
          const v = p.variants.find(x => x.available && (!c || x.options.includes(c))) || p.variants[0];
          total += v.price;
          const thumb = (v.featured_image && v.featured_image.src) || p.featured_image;
          const href = `${p.url}${c ? `?variant=${v.id}` : ''}`;
          return `<li><a href="${esc(href)}" class="daily__item">${thumb ? `<img src="${esc(thumb + (thumb.includes('?') ? '&' : '?') + 'width=160')}" alt="" width="64" height="80" loading="lazy">` : ''}<span><strong>${esc(p.title)}</strong>${c ? `<small>${esc(c)}</small>` : ''}</span><span class="daily__price">${money(v.price)}</span></a></li>`;
        }).join('');
        root.querySelector('[data-daily-total]').innerHTML = `Hela looken <strong>${money(total)}</strong>`;
        root.querySelector('[data-daily-buy]').href = `${root.dataset.outfitUrl}?outfit=${look.items.map(x => (x.color ? `${x.handle}~${encodeURIComponent(x.color)}` : x.handle)).join(',')}`;
        root.hidden = false;
        initReveals(root);
        if (hasGsap) ScrollTrigger.refresh();
        return;
      }
    });
  }

  /* ---------- Tacksidan: det kunden köpte och vad som passar till (Shopifys rekommendationer) ---------- */
  function initThanks(scope) {
    scope.querySelectorAll('[data-thanks]').forEach(async root => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const ids = [...new Set((new URLSearchParams(location.search).get('kop') || '').split(',').map(x => x.trim()).filter(x => /^\d+$/.test(x)))].slice(0, 12);
      let all = {};
      try { all = JSON.parse(document.querySelector('[data-thanks-products]').textContent); } catch { /* tom */ }
      const bought = ids.map(id => ({ id, ...all[id] })).filter(p => p.handle);
      if (!bought.length) return;
      const esc = t => String(t).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      root.querySelector('[data-thanks-list]').innerHTML = bought.map(p => `<li>${p.image ? `<img src="${esc(p.image)}" alt="" width="72" height="90">` : ''}<span><strong>${esc(p.title)}</strong>${p.type === 'Kläder' || p.type === 'Accessoarer' ? `<a href="${esc(root.dataset.outfitUrl)}?med=${esc(p.handle)}" class="link-line">Bygg en outfit med den →</a>` : `<a href="${esc(p.url)}" class="link-line">Se produkten</a>`}</span></li>`).join('');
      root.querySelector('[data-thanks-bought]').hidden = false;
      // Rekommendationer för varje köpt plagg, utan dubbletter och utan det som redan köpts
      const pages = await Promise.all(bought.map(p => fetch(`${root.dataset.recs}?section_id=related-products&product_id=${p.id}&limit=6`).then(r => (r.ok ? r.text() : '')).catch(() => '')));
      const boughtHandles = new Set(bought.map(p => p.handle));
      const cards = [], seen = new Set();
      // Varva förslagen så att varje köpt plagg får plats
      const lists = pages.map(html => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.card')]);
      for (let i = 0; i < 6 && cards.length < 8; i++) lists.forEach(l => {
        const c = l[i]; if (!c || cards.length >= 8) return;
        const h = c.querySelector('[data-fav]')?.dataset.fav || c.querySelector('a')?.getAttribute('href');
        if (!h || seen.has(h) || boughtHandles.has(h)) return;
        seen.add(h); cards.push(c);
      });
      if (!cards.length) return;
      const grid = root.querySelector('[data-thanks-grid]');
      cards.forEach(c => grid.append(document.importNode(c, true)));
      root.querySelector('[data-thanks-recs-wrap]').hidden = false;
      root.querySelector('[data-thanks-fallback]').hidden = true;
      paintFavs(grid); initNotes(grid); initCardReveal(grid);
      if (hasGsap) ScrollTrigger.refresh();
    });
  }

  /* ---------- Presentquiz: tre frågor, sedan filtreras de förrenderade produktkorten ---------- */
  function initGiftFinder(scope) {
    scope.querySelectorAll('[data-gift]').forEach(root => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const steps = [...root.querySelectorAll('[data-gift-step]')];
      const result = root.querySelector('[data-gift-result]');
      const grid = root.querySelector('[data-gift-grid]');
      const cards = [...grid.querySelectorAll('.card')];
      const note = root.querySelector('[data-gift-note]');
      const picked = name => root.querySelector(`input[name="${name}"]:checked`);
      const MAX = 8;

      function show(n, focus = true) {
        steps.forEach((s, k) => { s.hidden = k !== n; });
        result.hidden = n < steps.length;
        if (!focus) return;
        const target = n < steps.length ? steps[n].querySelector('legend') : result.querySelector('h2');
        target?.focus({ preventScroll: true });
        const top = root.getBoundingClientRect().top + scrollY - 120;
        if (Math.abs(scrollY - top) > 80) { if (lenis) lenis.scrollTo(top); else scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' }); }
      }
      function finish() {
        const who = picked('gift-who');
        const cat = picked('gift-cat');
        const budget = picked('gift-budget');
        const [min, max] = budget.value.split('-').map(Number);
        const inCat = c => cat.value === 'alla' || c.dataset.cat.split(' ').includes(cat.value);
        const inBudget = c => { const p = Number(c.dataset.price); return p >= min && (!max || p <= max); };
        let hits = cards.filter(c => inCat(c) && inBudget(c));
        let hint = '';
        if (!hits.length && cat.value !== 'alla') {
          const mid = max ? (min + max) / 2 : min;
          hits = cards.filter(inCat).sort((a, b) => Math.abs(a.dataset.price - mid) - Math.abs(b.dataset.price - mid));
          hint = 'Inget i den prisklassen just nu – här är det som ligger närmast.';
        }
        if (!hits.length) { hits = cards.filter(inBudget); hint = 'Här är det bästa vi har i din prisklass just nu.'; }
        if (!hint) hits.sort((a, b) => (b.dataset.new - a.dataset.new) || (Math.random() - 0.5));
        hits = hits.slice(0, MAX);
        cards.forEach(c => { c.hidden = !hits.includes(c); });
        hits.forEach(c => grid.append(c));
        root.querySelector('[data-gift-title]').textContent = `${who.dataset.say}, ${cat.dataset.say}`;
        root.querySelector('[data-gift-budget]').textContent = budget.dataset.say;
        note.textContent = hint || (hits.length ? '' : 'Vi hittade inget just nu – titta in i butiken eller ge ett presentkort.');
        note.hidden = !note.textContent;
        show(steps.length);
        initCardReveal(result);
        if (hasGsap) ScrollTrigger.refresh();
      }
      steps.forEach((s, k) => s.addEventListener('change', () => setTimeout(() => (k < steps.length - 1 ? show(k + 1) : finish()), reduce ? 0 : 280)));
      root.querySelectorAll('[data-gift-back]').forEach(b => b.addEventListener('click', () => show(Math.max(0, steps.indexOf(b.closest('[data-gift-step]')) - 1))));
      root.querySelector('[data-gift-restart]').addEventListener('click', () => {
        root.querySelectorAll('input:checked').forEach(i => { i.checked = false; });
        show(0);
      });
      // Från puffen på startsidan: ?till=mamma väljer svaret på fråga 1 och börjar på fråga 2
      const till = new URLSearchParams(location.search).get('till');
      const pre = till && root.querySelector(`input[name="gift-who"][value="${CSS.escape(till)}"]`);
      if (pre) { pre.checked = true; show(1, false); } else show(0, false);
    });
  }

  /* ---------- Start ---------- */
  /* Kollektionen renderas hel (slutsålda sist) – visa per_page åt gången med "Visa fler" */
  function initLoadMore(scope) {
    scope.querySelectorAll('[data-load-more]').forEach(grid => {
      const step = +grid.dataset.loadMore || 24;
      const cards = [...grid.children];
      const box = grid.nextElementSibling?.matches('.load-more') ? grid.nextElementSibling : null;
      if (!box || cards.length <= step) return;
      const btn = box.querySelector('[data-load-more-btn]');
      const count = box.querySelector('.load-more__count');
      let shown = step;
      const update = () => {
        cards.forEach((c, i) => c.classList.toggle('is-more', i >= shown));
        count.textContent = `Visar ${Math.min(shown, cards.length)} av ${cards.length}`;
        box.hidden = shown >= cards.length;
      };
      btn.addEventListener('click', () => {
        const first = cards[shown];
        shown += step;
        update();
        first?.querySelector('a')?.focus({ preventScroll: true });
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      });
      update();
    });
  }

  function init(scope = document) {
    initLoadMore(scope);
    initReveals(scope);
    initHero(scope);
    initMarquee(scope);
    initDoorway(scope);
    initHScroll(scope);
    initParallax(scope);
    initLookbook(scope);
    initCardReveal(scope);
    initColorize(scope);
    initMobileMotion(scope);
    initSwipeProgress(scope);
    initProduct(scope);
    initRecommendations(scope);
    initFavorites(scope);
    initGiftFinder(scope);
    initStories(scope);
    initThanks(scope);
    initSeasonal(scope);
    initDailyOutfit(scope);
    initNotes(scope);
    paintFavs(scope);
    paintStatus();
    paintExtraDays();
    if (hasGsap) ScrollTrigger.refresh();
  }

  initHeader();
  init();
  initKiosk();
  initPageMotion();
  const d = drawer();
  if (d) d.inert = true;
  setInterval(paintStatus, 30000);
  addEventListener('load', () => hasGsap && ScrollTrigger.refresh());

  // Temaeditorn: initiera om en sektion när den läggs till eller ändras
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', () => hasGsap && ScrollTrigger.refresh());
})();
